import { Check } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'

import { GraficoFinanceiro } from './grafico-financeiro'
import { GraficoInadimplencia } from './grafico-inadimplencia'
import { ragDeSaldo } from './saude'
import type { RelatorioFinanceiro, SerieFinanceira } from './types'

import { corDaSerie } from '@/features/relatorio/paleta'
import { cn } from '@/lib/cn'
import { inteiro, moeda, moedaCompacta, percentual } from '@/lib/formato'

const TOM: Record<'verde' | 'vermelho' | 'neutro', string> = {
  verde: 'text-success',
  vermelho: 'text-destructive',
  neutro: 'text-primary',
}

function Painel({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): ReactElement {
  return (
    <div className={cn('border-border bg-card/60 rounded-2xl border p-6', className)}>
      {children}
    </div>
  )
}

function Rotulo({ children }: { children: ReactNode }): ReactElement {
  return (
    <p className="text-muted-foreground text-sm font-semibold uppercase tracking-[0.16em]">
      {children}
    </p>
  )
}

function NumeroGrande({
  valor,
  tom,
}: {
  valor: string
  tom: 'verde' | 'vermelho' | 'neutro'
}): ReactElement {
  return <p className={cn('font-mono text-6xl font-semibold leading-none', TOM[tom])}>{valor}</p>
}

function LinhaEbitda({
  rotulo,
  valor,
  positivo,
}: {
  rotulo: string
  valor: string
  positivo: boolean
}): ReactElement {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xl">{rotulo}</span>
      <span className="flex items-center gap-3">
        <span
          className={cn(
            'font-mono text-4xl font-semibold',
            positivo ? 'text-success' : 'text-destructive',
          )}
        >
          {valor}
        </span>
        <span
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full border',
            positivo ? 'border-success text-success' : 'border-destructive text-destructive',
          )}
        >
          <Check className="h-5 w-5" aria-hidden />
        </span>
      </span>
    </div>
  )
}

function MiniMetrica({
  rotulo,
  valor,
  tom = 'neutro',
}: {
  rotulo: string
  valor: string
  tom?: 'verde' | 'vermelho' | 'neutro'
}): ReactElement {
  return (
    <div className="border-border bg-background/30 rounded-xl border p-4 text-center">
      <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
        {rotulo}
      </p>
      <p className={cn('mt-2 font-mono text-3xl font-semibold', TOM[tom])}>{valor}</p>
    </div>
  )
}

export interface PainelDensoFinanceiroProps {
  relatorio: RelatorioFinanceiro
  serie?: SerieFinanceira
}

/** Uma tela cheia densa do Bloco 1 para o modo apresentação (ref. ENJOY BUSINESS). */
export function PainelDensoFinanceiro({
  relatorio,
  serie,
}: PainelDensoFinanceiroProps): ReactElement {
  const pontos = serie?.pontos ?? []
  const maxCusto = Math.max(...relatorio.custoPorProduto.map((c) => c.custo), 1)

  return (
    <div className="space-y-6 p-2">
      <header className="border-primary/40 flex items-end justify-between border-b pb-4">
        <div>
          <h1 className="text-primary text-5xl font-semibold tracking-tight">Saúde Financeira</h1>
          <p className="text-muted-foreground mt-2 text-xl">
            Resumo executivo — {relatorio.periodo.label}
          </p>
        </div>
        <span className="text-primary text-3xl font-light tracking-[0.3em]">ENJOY BUSINESS</span>
      </header>

      <div className="grid grid-cols-3 gap-6">
        <Painel className="col-span-2">
          <Rotulo>Receita — recebido (caixa) vs. faturado (competência)</Rotulo>
          <div className="mt-2 flex flex-wrap items-end gap-x-10 gap-y-3">
            <div>
              <NumeroGrande valor={moedaCompacta(relatorio.receitaRealizada)} tom="neutro" />
              <p className="text-muted-foreground mt-1 text-sm">Recebido no período</p>
            </div>
            <div className="mb-1">
              <p className="text-muted-foreground font-mono text-4xl font-semibold">
                {moedaCompacta(relatorio.receitaEconomica)}
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                Faturado
                {relatorio.receitaEconomica > 0
                  ? ` · ${percentual(relatorio.receitaRealizada / relatorio.receitaEconomica, 0)} recebido`
                  : ''}
              </p>
            </div>
          </div>
          <div className="mt-4 h-56">
            <GraficoFinanceiro pontos={pontos} />
          </div>
        </Painel>

        <Painel>
          <Rotulo>EBITDA do mês</Rotulo>
          <div className="mt-6 space-y-6">
            <LinhaEbitda
              rotulo="Econômico"
              valor={moedaCompacta(relatorio.ebitdaEconomico)}
              positivo={relatorio.ebitdaEconomico >= 0}
            />
            <div className="border-border border-t" />
            <LinhaEbitda
              rotulo="Monetário"
              valor={moedaCompacta(relatorio.ebitdaMonetario)}
              positivo={relatorio.ebitdaMonetario >= 0}
            />
          </div>
        </Painel>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <Painel>
          <Rotulo>Liquidez e caixa</Rotulo>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <MiniMetrica
              rotulo="Caixa 90 dias"
              valor={moedaCompacta(relatorio.caixaProjetado90d)}
              tom={ragDeSaldo(relatorio.caixaProjetado90d) === 'verde' ? 'verde' : 'vermelho'}
            />
            <MiniMetrica
              rotulo="Posição líquida"
              valor={moedaCompacta(relatorio.posicaoLiquida)}
              tom={ragDeSaldo(relatorio.posicaoLiquida) === 'verde' ? 'verde' : 'vermelho'}
            />
            <MiniMetrica rotulo="Saldo em contas" valor={moedaCompacta(relatorio.saldoContas)} />
            <MiniMetrica rotulo="PMR" valor={`${inteiro(relatorio.pmrDias)} dias`} />
          </div>
        </Painel>

        <Painel className="col-span-2">
          <Rotulo>Inadimplência e recuperação</Rotulo>
          <div className="mt-4 grid grid-cols-[minmax(0,240px)_1fr] gap-6">
            <div className="flex flex-col justify-center gap-6">
              <div>
                <p className="text-muted-foreground text-sm">Inadimplência total</p>
                <NumeroGrande valor={moeda(relatorio.inadimplencia)} tom="vermelho" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Taxa de recuperação</p>
                <NumeroGrande valor={percentual(relatorio.taxaRecuperacao, 0)} tom="verde" />
              </div>
            </div>
            <div className="h-64">
              <GraficoInadimplencia pontos={pontos} />
            </div>
          </div>
        </Painel>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <Painel>
          <Rotulo>Em aberto no período</Rotulo>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <MiniMetrica
              rotulo="A receber"
              valor={moedaCompacta(relatorio.contasAReceber)}
              tom="verde"
            />
            <MiniMetrica
              rotulo="A pagar"
              valor={moedaCompacta(relatorio.contasAPagar)}
              tom="vermelho"
            />
          </div>
        </Painel>

        <Painel>
          <Rotulo>Margem (proxy — Conta Azul)</Rotulo>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <MiniMetrica
              rotulo="Bruta"
              valor={
                relatorio.margem.brutaPct !== null ? percentual(relatorio.margem.brutaPct, 0) : '—'
              }
              tom={relatorio.margem.bruta >= 0 ? 'verde' : 'vermelho'}
            />
            <MiniMetrica
              rotulo="Contribuição"
              valor={
                relatorio.margem.contribuicaoPct !== null
                  ? percentual(relatorio.margem.contribuicaoPct, 0)
                  : '—'
              }
              tom={relatorio.margem.contribuicao >= 0 ? 'verde' : 'vermelho'}
            />
          </div>
        </Painel>

        <Painel>
          <Rotulo>MRR recorrente (Conta Azul)</Rotulo>
          <div className="mt-2">
            <NumeroGrande
              valor={relatorio.mrr.atual > 0 ? moedaCompacta(relatorio.mrr.atual) : '—'}
              tom="neutro"
            />
          </div>
          <p className="text-muted-foreground mt-3 text-xl">
            {relatorio.mrr.variacao !== null
              ? `${relatorio.mrr.variacao >= 0 ? '+' : ''}${percentual(relatorio.mrr.variacao, 0)} vs. mês anterior`
              : 'Comunidades, club e anuidade'}
          </p>
        </Painel>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <Painel className="col-span-2">
          <Rotulo>Custo direto por produto (Conta Azul)</Rotulo>
          {relatorio.custoPorProduto.length === 0 ? (
            <p className="text-muted-foreground mt-4 text-xl">Sem custo direto no período.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {relatorio.custoPorProduto.slice(0, 5).map((item, i) => (
                <li key={item.produto}>
                  <div className="flex items-baseline justify-between gap-4 text-xl">
                    <span className="min-w-0 truncate">{item.produto}</span>
                    <span className="text-muted-foreground shrink-0 font-mono">
                      {moedaCompacta(item.custo)}
                    </span>
                  </div>
                  <div className="bg-muted mt-1.5 h-3 overflow-hidden rounded-full">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(item.custo / maxCusto) * 100}%`, background: corDaSerie(i) }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Painel>

        <Painel>
          <Rotulo>Contas financeiras</Rotulo>
          {relatorio.contas.length === 0 ? (
            <p className="text-muted-foreground mt-4 text-xl">Nenhuma conta ativa.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {relatorio.contas.slice(0, 5).map((conta) => (
                <li key={conta.id} className="flex items-baseline justify-between gap-4 text-lg">
                  <span className="min-w-0 truncate">{conta.nome}</span>
                  <span className="shrink-0 font-mono">{moedaCompacta(conta.saldo)}</span>
                </li>
              ))}
              <li className="border-border flex items-baseline justify-between gap-4 border-t pt-2 text-xl font-semibold">
                <span>Total</span>
                <span className="font-mono">{moedaCompacta(relatorio.saldoContas)}</span>
              </li>
            </ul>
          )}
        </Painel>
      </div>
    </div>
  )
}
